import { useQuery } from "@tanstack/react-query";
import { fetchAllCreditCosts } from "@/services/db/creditCosts";
import type { ModelInfo } from "@shared/schemas/models";

type CostMap = Record<string, number>;  // "operation:model:variant" -> cost

export function useCreditCosts() {
  const { data: costMap = {}, isLoading } = useQuery<CostMap>({
    queryKey: ["credit-costs"],
    queryFn: async () => {
      const rows = await fetchAllCreditCosts();
      const map: CostMap = {};
      for (const row of rows) {
        // Model-specific key: "operation:model:variant"
        map[`${row.operation}:${row.model}:${row.variant}`] = row.cost;
        // Generic key (no model) — used by video lookups which don't take a model param
        const genericKey = `${row.operation}:${row.variant}`;
        if (!(genericKey in map)) map[genericKey] = row.cost;
      }
      return map;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const getCost = (operation: string, variant: string, model?: string): number => {
    if (model) {
      return costMap[`${operation}:${model}:${variant}`] ?? -1;
    }
    return costMap[`${operation}:${variant}`] ?? -1;
  };

  return {
    getImageGenerateCost: (res: string, model?: string) => getCost("image_generate", res, model),
    getImageEditCost:     (res: string, model?: string) => getCost("image_edit", res, model),
    getVideoCost:         (dur: number, res: string, model?: string, audio?: boolean) => getCost("video_generate", `${res}_${dur}s${audio ? "_audio" : ""}`, model),
    getDirectorCost:      (resolution: string, model?: string) => getCost("director_generate", resolution, model),
    /** Resolve image model cost for the selector — falls back to the model's first supported resolution when current one doesn't apply. */
    getImageModelCost:    (modelId: string, models: ModelInfo[], currentResolution: string, operation: string = "image_generate") => {
      const model = models.find((m) => m.id === modelId);
      const resolutions = model?.capabilities.supportedResolutions;
      const res = resolutions?.includes(currentResolution) ? currentResolution : resolutions?.[0] ?? currentResolution;
      return getCost(operation, res, modelId);
    },
    /** Resolve video model cost for the selector — falls back to the model's first supported duration/resolution when current settings don't apply. */
    getVideoModelCost:    (modelId: string, models: ModelInfo[], currentDuration: number, currentResolution: string) => {
      const model = models.find((m) => m.id === modelId);
      const durations = model?.capabilities.supportedDurations;
      const resolutions = model?.capabilities.supportedResolutions;
      const dur = durations?.includes(currentDuration) ? currentDuration : durations?.[0] ?? currentDuration;
      const res = resolutions?.includes(currentResolution) ? currentResolution : resolutions?.[0] ?? currentResolution;
      return getCost("video_generate", `${res}_${dur}s`, modelId);
    },
    getCost,
    costsLoading: isLoading,
  };
}
